import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import styles from "./Skeleton.module.scss";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks/useLayoutPropsUtil";

/**
 * Props for the SkeletonPill component
 * @extends Omit<ComponentPropsWithoutRef<"div">, "children">
 * @extends Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink">
 */
export type SkeletonPillProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "children"
> &
  Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink"> & {
    /**
     * The width of the pill skeleton in CSS units.
     */
    width?: string;
  };

/**
 * SkeletonPill component for displaying pill-shaped loading placeholders.
 *
 * Features:
 * - Pill-shaped skeleton placeholder with customizable width
 * - Animated pulsing effect for loading indication
 * - Accessible with proper ARIA attributes
 * - Layout utility props for positioning and spacing
 * - Customizable styling through className and style props
 * - Progress bar role for screen readers
 * - Large border radius for pill appearance
 * - Fixed height for consistent styling
 *
 * @example
 * <Skeleton.Pill width="120px" />
 *
 * <Skeleton.Pill
 *   width="8rem"
 *   className="custom-skeleton"
 *   aria-label="Loading tag"
 * />
 */
export const SkeletonPill = forwardRef<HTMLDivElement, SkeletonPillProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      width,
      "aria-label": ariaLabel = "Loading content",
      style,
      ...rest
    } = componentProps;

    const SkeletonClassNames = cx(
      [styles["skeleton"]],
      [styles["pill"]],
      styles["shade"],
      className,
    );

    const styleCombined = {
      ...style,
      ...layoutStyles,
      "--width": width,
    };

    return (
      <div
        {...rest}
        data-anv="skeleton-pill"
        role="progressbar"
        aria-busy="true"
        className={SkeletonClassNames}
        style={styleCombined}
        aria-label={ariaLabel}
        ref={ref}
      />
    );
  },
);

SkeletonPill.displayName = "SkeletonPill";
